import { useState, useRef, useEffect, useCallback } from "react";
import externalLinkIcon from "@/assets/external-link-icon.png";
import realEstateImg from "@/assets/real-estate-mobile-hd.png";
import consultingImg from "@/assets/consulting-mobile-hd.png";
import lawImg from "@/assets/law-mobile-hd.png";
import accountingImg from "@/assets/accounting-mobile-hd.png";
import financeImg from "@/assets/finance-mobile-hd.png";
import insuranceImg from "@/assets/insurance-mobile-hd.png";

/**
 * PROFILE URL CONFIGURATION
 * Swap the default URL or add new profiles here
 */
const PROFILE_URLS = [
  {
    id: "michaela",
    label: "Real Estate",
    url: "https://card.qcardenterprise.com/c/michaela_zuckerberg_427545949",
    // Approximate page height in iframe coordinates (tune per profile)
    contentHeight: 7191,
  },
  {
    id: "capgemini",
    label: "Consulting",
    url: "https://card.qcardenterprise.com/c/test_capgemini_1228503402",
    // Approximate page height in iframe coordinates (tune per profile)
    // Adjusted so "Powered by QCard" footer is visible at bottom when fully scrolled
    contentHeight: 5600,
  },
  {
    id: "lplegal",
    label: "Law",
    url: "https://card.qcardenterprise.com/c/test_lplegal_1021874569",
    // Approximate page height in iframe coordinates (tune per profile)
    contentHeight: 4900,
  },
  {
    id: "fiona",
    label: "Accounting",
    url: "https://card.qcardenterprise.com/c/fiona_grossman_1093779199",
    // Approximate page height in iframe coordinates (tune per profile)
    // Adjusted to stop scrolling when "Powered by QCard" footer appears
    contentHeight: 5800,
  },
  {
    id: "capco",
    label: "Finance",
    url: "https://card.qcardenterprise.com/c/test_capco_275121231",
    // Approximate page height in iframe coordinates (tune per profile)
    contentHeight: 5400,
  },
  {
    id: "noah",
    label: "Insurance",
    url: "https://card.qcardenterprise.com/c/noah_hayes_751617936",
    // Approximate page height in iframe coordinates (tune per profile)
    contentHeight: 5300,
  },
] as const;

const PROFILE_IMAGES = [
  {
    id: "real-estate",
    label: "Real Estate",
    image: realEstateImg,
    contentHeight: 7300,
  },
  {
    id: "consulting",
    label: "Consulting",
    image: consultingImg,
    contentHeight: 5600,
  },
  {
    id: "law",
    label: "Law",
    image: lawImg,
    contentHeight: 4900,
  },
  {
    id: "accounting",
    label: "Accounting",
    image: accountingImg,
    contentHeight: 5800,
  },
  {
    id: "finance",
    label: "Finance",
    image: financeImg,
    contentHeight: 5400,
  },
  {
    id: "insurance",
    label: "Insurance",
    image: insuranceImg,
    contentHeight: 5300,
  },
];

// Base "device" viewport height in iframe coordinates (9:19.5 at 375px wide)
const BASE_VIEWPORT_HEIGHT = 812;

// If you ever add new profiles and forget to set contentHeight, we fall back to this.
const DEFAULT_PROFILE_CONTENT_HEIGHT = 5600;
// Friction coefficient for momentum (0-1, lower = more friction)
const FRICTION = 0.92;
// Minimum velocity before stopping momentum
const MIN_VELOCITY = 0.5;
// Wheel scroll smoothing factor (0-1, higher = smoother but slower response)
const WHEEL_SMOOTHING = 0.15;
// Wheel velocity decay per frame
const WHEEL_FRICTION = 0.88;

interface InteractivePhoneMockupProps {
  /** Index of the default profile to show (0-3) */
  defaultProfileIndex?: number;
  /** Whether to show profile switching buttons */
  showProfileSwitcher?: boolean;
  /** Custom class name for the container */
  className?: string;
}

/**
 * Interactive Phone Mockup Component
 *
 * Displays a realistic iPhone frame with a fully interactive iframe inside.
 *
 * Features:
 * - Realistic phone frame with CSS (rounded corners, shadow, 9:19.5 aspect ratio)
 * - Click-and-drag scrolling with momentum/inertia
 * - Mouse wheel scrolling
 * - Responsive scaling for all screen sizes
 * - Optional profile switcher buttons
 *
 * Note: Since cross-origin iframes cannot have their scroll controlled externally,
 * we simulate scrolling by translating the iframe position within a clipped container.
 */
export function InteractivePhoneMockup({
  defaultProfileIndex = 0,
  showProfileSwitcher = true,
  className = "",
}: InteractivePhoneMockupProps) {
  const [selectedProfile, setSelectedProfile] = useState(defaultProfileIndex);
  const [iframeScale, setIframeScale] = useState(0.76);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [loadedProfiles, setLoadedProfiles] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const screenRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    startY: 0,
    startOffset: 0,
    lastY: 0,
    lastTime: 0,
    velocity: 0,
    hasMoved: false,
  });
  const momentumRef = useRef<number | null>(null);
  const wheelVelocityRef = useRef(0);
  const wheelAnimationRef = useRef<number | null>(null);

  // Use a ref to track scroll offset for immediate access in event handlers
  const scrollOffsetRef = useRef(scrollOffset);
  useEffect(() => {
    scrollOffsetRef.current = scrollOffset;
  }, [scrollOffset]);

  const activeProfile = PROFILE_URLS[selectedProfile];
  const imageProfile = PROFILE_IMAGES[selectedProfile];

  const contentHeight = imageProfile?.contentHeight ?? activeProfile.contentHeight ?? DEFAULT_PROFILE_CONTENT_HEIGHT;
  const maxScrollLimit = Math.max(0, contentHeight - BASE_VIEWPORT_HEIGHT);

  // Also keep maxScrollLimit in a ref for immediate access
  const maxScrollLimitRef = useRef(maxScrollLimit);
  useEffect(() => {
    maxScrollLimitRef.current = maxScrollLimit;
  }, [maxScrollLimit]);

  // Handle iframe load events
  // IMPORTANT: We add a settling delay after onLoad to let the iframe's internal
  // content fully settle (fonts, images, JS-rendered "About" and "Social Media"
  // sections). On Slow 4G (Lighthouse mobile), the iframe's internal content
  // continues shifting for several seconds after onLoad fires. We keep the iframe
  // wrapper display:none during this entire period so those shifts can't be measured
  // by Lighthouse's CLS calculation (display:none removes from render tree).
  // Heavier profiles (Real Estate with listing images, Accounting with KPMG assets)
  // need longer settling time than lighter ones (Law).
  const handleIframeLoad = useCallback(
    (profileIndex: number) => {
      // Reduced from 15s to 2s. The 15s delay was eliminating CLS but made the
      // mockup feel broken — users saw a skeleton for 15 seconds. The iframe is
      // wrapped in contain:strict which already isolates most internal layout
      // shifts from the parent page's CLS score. 2s is enough for the iframe's
      // initial JS to execute and render the main content structure.
      // Trade-off: minor CLS risk on very heavy profiles vs much faster perceived load.
      const settlingDelay = 2000;
      setTimeout(() => {
        setLoadedProfiles((prev) => new Set(prev).add(profileIndex));
        if (profileIndex === selectedProfile) {
          setIsLoading(false);
        }
      }, settlingDelay);
    },
    [selectedProfile],
  );

  // Update loading state when switching profiles
  useEffect(() => {
    if (loadedProfiles.has(selectedProfile)) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    // Reset scroll when switching profiles
    setScrollOffset(0);
  }, [selectedProfile, loadedProfiles]);

  // Calculate scale based on the *rendered* screen container width.
  // IMPORTANT: use getBoundingClientRect() so this still works when a parent applies CSS transforms
  // (some builders/embeds can scale the page without changing offsetWidth).
  // Using 375 (standard iPhone width) to match iframe content width exactly.
  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;

    let lastWidth = 0;
    let rafId: number | null = null;
    let remeasureTimeouts: ReturnType<typeof setTimeout>[] = [];

    const calculateScale = () => {
      const node = screenRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const containerWidth = rect.width || node.offsetWidth;
      if (!containerWidth || containerWidth < 10) return; // Skip if not visible yet

      // Only update if width actually changed (avoid unnecessary re-renders)
      if (Math.abs(containerWidth - lastWidth) > 0.5) {
        lastWidth = containerWidth;
        setIframeScale(containerWidth / 375);
      }
    };

    // Initial calculation
    calculateScale();

    // Post-mount remeasure loop to catch delayed layout changes (Webflow breakpoints, etc.)
    // Measure at 50ms, 150ms, 300ms, 500ms, 1000ms after mount
    [50, 150, 300, 500, 1000].forEach((delay) => {
      remeasureTimeouts.push(setTimeout(calculateScale, delay));
    });

    // Track element size changes
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(calculateScale);
    });
    ro.observe(el);

    // Also observe parent containers (Webflow may resize parents)
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      ro.observe(parent);
      parent = parent.parentElement;
    }

    // Track viewport changes
    window.addEventListener("resize", calculateScale);
    window.visualViewport?.addEventListener("resize", calculateScale);
    window.visualViewport?.addEventListener("scroll", calculateScale);

    // Track orientation changes (mobile)
    const orientationHandler = () => {
      // Delay to let layout settle after orientation change
      setTimeout(calculateScale, 100);
      setTimeout(calculateScale, 300);
    };
    window.addEventListener("orientationchange", orientationHandler);

    // Track visibility changes (tab switching, iframe becoming visible)
    const visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        setTimeout(calculateScale, 50);
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    // For iframes: listen for parent window messages that might indicate resize
    const messageHandler = (e: MessageEvent) => {
      if (e.data?.type === "resize" || e.data?.resize) {
        setTimeout(calculateScale, 50);
      }
    };
    window.addEventListener("message", messageHandler);

    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      remeasureTimeouts.forEach(clearTimeout);
      window.removeEventListener("resize", calculateScale);
      window.visualViewport?.removeEventListener("resize", calculateScale);
      window.visualViewport?.removeEventListener("scroll", calculateScale);
      window.removeEventListener("orientationchange", orientationHandler);
      document.removeEventListener("visibilitychange", visibilityHandler);
      window.removeEventListener("message", messageHandler);
    };
  }, []);

  // Reset scroll when profile changes
  useEffect(() => {
    setScrollOffset(0);
  }, [selectedProfile]);

  // Simple scroll limit - stops at "Powered by Qcard" section
  const getMaxScroll = useCallback(() => {
    return maxScrollLimit;
  }, [maxScrollLimit]);

  const clampOffset = useCallback((offset: number, maxScroll: number) => {
    return Math.max(0, Math.min(maxScroll, offset));
  }, []);

  // Start momentum animation
  const startMomentum = useCallback(
    (initialVelocity: number) => {
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current);
      }

      let velocity = initialVelocity;
      let lastTime = performance.now();

      const animate = () => {
        const now = performance.now();
        const dt = Math.min((now - lastTime) / 16, 2); // Normalize to ~60fps, cap delta
        lastTime = now;

        velocity *= Math.pow(FRICTION, dt);

        if (Math.abs(velocity) < MIN_VELOCITY) {
          momentumRef.current = null;
          return;
        }

        setScrollOffset((prev) => {
          const desired = prev + velocity * dt;
          const maxScroll = getMaxScroll();
          return clampOffset(desired, maxScroll);
        });
        momentumRef.current = requestAnimationFrame(animate);
      };

      momentumRef.current = requestAnimationFrame(animate);
    },
    [getMaxScroll, clampOffset],
  );

  // Stop momentum animation
  const stopMomentum = useCallback(() => {
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }
  }, []);

  // Stop wheel animation
  const stopWheelAnimation = useCallback(() => {
    if (wheelAnimationRef.current) {
      cancelAnimationFrame(wheelAnimationRef.current);
      wheelAnimationRef.current = null;
    }
    wheelVelocityRef.current = 0;
  }, []);

  // Start smooth wheel scrolling animation
  const startWheelAnimation = useCallback(() => {
    if (wheelAnimationRef.current) return; // Already running

    const animate = () => {
      const velocity = wheelVelocityRef.current;

      if (Math.abs(velocity) < MIN_VELOCITY) {
        wheelAnimationRef.current = null;
        wheelVelocityRef.current = 0;
        return;
      }

      // Apply friction
      wheelVelocityRef.current *= WHEEL_FRICTION;

      setScrollOffset((prev) => {
        const desired = prev + velocity;
        const maxScroll = getMaxScroll();
        return clampOffset(desired, maxScroll);
      });

      wheelAnimationRef.current = requestAnimationFrame(animate);
    };

    wheelAnimationRef.current = requestAnimationFrame(animate);
  }, [getMaxScroll, clampOffset]);

  // Handle mouse enter for hover state
  const handleMouseEnter = useCallback(() => {
    // Can be used for hover effects if needed
  }, []);

  // Simulate a click on the iframe at the given coordinates
  // Handle mouse down
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      stopMomentum();

      // Hide drag indicator on first interaction
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      const now = performance.now();
      dragRef.current = {
        startY: e.clientY,
        startOffset: scrollOffset,
        lastY: e.clientY,
        lastTime: now,
        velocity: 0,
        hasMoved: false,
      };
      setIsDragging(true);
    },
    [scrollOffset, stopMomentum, hasInteracted],
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;

      const now = performance.now();
      const deltaY = dragRef.current.lastY - e.clientY;
      const deltaTime = now - dragRef.current.lastTime;

      // Track if we've moved significantly (reduced threshold for better responsiveness)
      if (Math.abs(e.clientY - dragRef.current.startY) > 3) {
        dragRef.current.hasMoved = true;
      }

      // Calculate velocity (pixels per frame at 60fps)
      if (deltaTime > 0) {
        const instantVelocity = (deltaY / deltaTime) * 16;
        // Smooth velocity with previous readings
        dragRef.current.velocity = dragRef.current.velocity * 0.7 + instantVelocity * 0.3;
      }

      dragRef.current.lastY = e.clientY;
      dragRef.current.lastTime = now;

      // Update scroll position - account for scale
      const scaledDelta = deltaY / iframeScale;
      setScrollOffset((prev) => {
        const desired = prev + scaledDelta;
        const maxScroll = getMaxScroll();
        return clampOffset(desired, maxScroll);
      });

      // Hide the drag indicator after first scroll
      if (!hasScrolled && Math.abs(deltaY) > 0) {
        setHasScrolled(true);
      }
    },
    [isDragging, iframeScale, getMaxScroll, clampOffset, hasScrolled],
  );

  // Handle mouse up with single-click to show dialog
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;

      const wasDrag = dragRef.current.hasMoved;

      // Apply momentum if we were moving fast enough
      if (Math.abs(dragRef.current.velocity) > MIN_VELOCITY && wasDrag) {
        startMomentum(dragRef.current.velocity);
      }

      setIsDragging(false);

      // Single-click detection (only if not dragging)
      if (!wasDrag) {
        setShowOpenDialog(true);
      }
    },
    [isDragging, startMomentum],
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      // Apply momentum on leave too
      if (Math.abs(dragRef.current.velocity) > MIN_VELOCITY) {
        startMomentum(dragRef.current.velocity);
      }
      setIsDragging(false);
    }
  }, [isDragging, startMomentum]);

  // Handle wheel scroll with smooth momentum (native event version)
  const handleWheelNative = useCallback(
    (e: WheelEvent) => {
      // Use refs for immediate access to current values
      const currentOffset = scrollOffsetRef.current;
      const currentMaxScroll = maxScrollLimitRef.current;

      const isAtBottom = currentOffset >= currentMaxScroll - 5;
      const isScrollingDown = e.deltaY > 0;

      // If at the bottom and trying to scroll down, let the page scroll
      if (isAtBottom && isScrollingDown) {
        return; // Don't prevent default, allow page scroll
      }

      // If at the top and trying to scroll up, let the page scroll
      const isAtTop = currentOffset <= 5;
      const isScrollingUp = e.deltaY < 0;
      if (isAtTop && isScrollingUp) {
        return; // Don't prevent default, allow page scroll
      }

      e.preventDefault();
      e.stopPropagation();
      stopMomentum(); // Stop drag momentum if any

      // Hide drag indicator on first interaction
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      // Normalize wheel delta and account for scale
      const normalizedDelta = e.deltaY / iframeScale;

      // Add to velocity with smoothing (accumulate wheel input)
      wheelVelocityRef.current += normalizedDelta * WHEEL_SMOOTHING;

      // Start the smooth scroll animation if not already running
      startWheelAnimation();
    },
    [iframeScale, stopMomentum, startWheelAnimation, hasInteracted],
  );

  // Native touch event handlers with preventDefault to stop page scroll
  const handleTouchStartNative = useCallback(
    (e: TouchEvent) => {
      stopMomentum();

      // Hide drag indicator on first interaction
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      const touch = e.touches[0];

      dragRef.current = {
        startY: touch.clientY,
        startOffset: scrollOffset,
        lastY: touch.clientY,
        lastTime: performance.now(),
        velocity: 0,
        hasMoved: false,
      };
      setIsDragging(true);
    },
    [scrollOffset, stopMomentum, hasInteracted],
  );

  const handleTouchMoveNative = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      const deltaY = dragRef.current.lastY - touch.clientY;

      // Use refs for immediate access to current values
      const currentOffset = scrollOffsetRef.current;
      const currentMaxScroll = maxScrollLimitRef.current;

      const isAtBottom = currentOffset >= currentMaxScroll - 5;
      const isScrollingDown = deltaY > 0;

      // If at the bottom and trying to scroll down, let the page scroll
      if (isAtBottom && isScrollingDown) {
        return; // Don't prevent default, allow page scroll
      }

      // If at the top and trying to scroll up, let the page scroll
      const isAtTop = currentOffset <= 5;
      const isScrollingUp = deltaY < 0;
      if (isAtTop && isScrollingUp) {
        return; // Don't prevent default, allow page scroll
      }

      // Prevent page scroll - only when we're in the middle of content
      e.preventDefault();

      const now = performance.now();
      const deltaTime = now - dragRef.current.lastTime;

      if (Math.abs(touch.clientY - dragRef.current.startY) > 3) {
        dragRef.current.hasMoved = true;
      }

      if (deltaTime > 0) {
        const instantVelocity = (deltaY / deltaTime) * 16;
        dragRef.current.velocity = dragRef.current.velocity * 0.7 + instantVelocity * 0.3;
      }

      dragRef.current.lastY = touch.clientY;
      dragRef.current.lastTime = now;

      const scaledDelta = deltaY / iframeScale;
      setScrollOffset((prev) => {
        const desired = prev + scaledDelta;
        const maxScroll = getMaxScroll();
        return clampOffset(desired, maxScroll);
      });

      // Hide the drag indicator after first scroll
      if (!hasScrolled && Math.abs(deltaY) > 0) {
        setHasScrolled(true);
      }
    },
    [isDragging, iframeScale, getMaxScroll, clampOffset, hasScrolled],
  );

  const handleTouchEndNative = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;

      const wasDrag = dragRef.current.hasMoved;

      if (Math.abs(dragRef.current.velocity) > MIN_VELOCITY && wasDrag) {
        startMomentum(dragRef.current.velocity);
      }

      setIsDragging(false);

      // Single-tap detection (only if not dragging)
      if (!wasDrag) {
        setShowOpenDialog(true);
      }
    },
    [isDragging, startMomentum],
  );

  // Attach native wheel and touch event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const element = screenRef.current;
    if (!element) return;

    element.addEventListener("wheel", handleWheelNative, { passive: false });
    element.addEventListener("touchstart", handleTouchStartNative, { passive: false });
    element.addEventListener("touchmove", handleTouchMoveNative, { passive: false });
    element.addEventListener("touchend", handleTouchEndNative, { passive: false });

    return () => {
      element.removeEventListener("wheel", handleWheelNative);
      element.removeEventListener("touchstart", handleTouchStartNative);
      element.removeEventListener("touchmove", handleTouchMoveNative);
      element.removeEventListener("touchend", handleTouchEndNative);
    };
  }, [handleWheelNative, handleTouchStartNative, handleTouchMoveNative, handleTouchEndNative]);

  // Global mouse up handler
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        if (Math.abs(dragRef.current.velocity) > MIN_VELOCITY) {
          startMomentum(dragRef.current.velocity);
        }
        setIsDragging(false);
      }
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isDragging, startMomentum]);

  // Cleanup animations on unmount
  useEffect(() => {
    return () => {
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current);
      }
      if (wheelAnimationRef.current) {
        cancelAnimationFrame(wheelAnimationRef.current);
      }
    };
  }, []);

  // Defer iframe loading until AFTER initial paint completes
  // On mobile, the mockup is in the viewport immediately, so IntersectionObserver
  // fires instantly and loads the heavy iframe JS during initial render.
  // Using requestIdleCallback ensures the browser finishes painting first,
  // cutting TBT by ~2,500ms on industry pages.
  useEffect(() => {
    const rIC = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 150));
    const cancelRIC = window.cancelIdleCallback || clearTimeout;
    let idleHandle: number | null = null;

    // Wait for initial paint to complete, then load iframes when main thread is idle
    // The 100ms minimum delay ensures FCP/LCP happen before iframe JS executes
    const minDelayTimer = setTimeout(() => {
      idleHandle = rIC(
        () => {
          setIsNearViewport(true);
        },
        { timeout: 3000 },
      ); // Max wait 3s even if never idle
    }, 100);

    return () => {
      clearTimeout(minDelayTimer);
      if (idleHandle !== null) cancelRIC(idleHandle);
    };
  }, []);

  const thumbPercent = Math.max(10, (BASE_VIEWPORT_HEIGHT / contentHeight) * 100);
  const thumbTravelPercent = 100 - thumbPercent;
  const thumbTopPercent = maxScrollLimit > 0 ? (scrollOffset / maxScrollLimit) * thumbTravelPercent : 0;

  // Handle opening profile in new tab
  const handleOpenProfile = useCallback(() => {
    window.open(activeProfile.url, "_blank", "noopener,noreferrer");
    setShowOpenDialog(false);
  }, [activeProfile.url]);

  return (
    <div ref={containerRef} className={`flex flex-col items-center w-full ${className}`}>
      {/* Phone Container - Responsive sizing with fixed aspect ratio */}
      <div className="relative w-full max-w-[300px] sm:max-w-[320px] mx-auto">
        {/* Glow Effect Behind Phone */}
        <div className="absolute -inset-4 bg-gradient-to-br from-enterprise-purple/20 to-enterprise-purple-dark/10 rounded-[2rem] blur-xl pointer-events-none" />

        {/* iPhone Frame */}
        <div className="relative bg-[#1a1a1a] rounded-[3rem] p-2 shadow-2xl border border-border/30">
          {/* Screen Container with locked 9:19.5 aspect ratio - prevents reflow */}
          <div
            ref={screenRef}
            className="relative bg-white rounded-[2.5rem] overflow-hidden select-none"
            style={{
              aspectRatio: "9 / 19.5",
              // Prevent any content-based resizing
              contain: "strict",
              // Enable GPU acceleration for smoother transforms
              willChange: "transform",
              // Ensure crisp rendering on high-DPI displays
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
            }}
          >
            {/* Status Bar */}
            <div className="absolute top-0 left-0 right-0 bg-black/90 px-5 py-2 flex items-center justify-between z-10 pointer-events-none">
              <span className="text-[10px] text-white font-medium">9:41</span>
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2 17h2v4H2zm4-5h2v9H6zm4-4h2v13h-2zm4-4h2v17h-2zm4-2h2v19h-2z" />
                </svg>
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 18c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-4.24-2.76C8.94 14.06 10.4 13.5 12 13.5s3.06.56 4.24 1.74l1.42-1.42C15.93 12.09 14.04 11.5 12 11.5s-3.93.59-5.66 1.82l1.42 1.42zM12 9.5c2.47 0 4.73.94 6.42 2.48l1.42-1.42C17.68 8.52 14.97 7.5 12 7.5S6.32 8.52 4.16 10.56l1.42 1.42C7.27 10.44 9.53 9.5 12 9.5zm0-4c3.36 0 6.45 1.24 8.82 3.28l1.42-1.42C19.65 5.06 15.99 3.5 12 3.5S4.35 5.06 1.76 7.36l1.42 1.42C5.55 6.74 8.64 5.5 12 5.5z" />
                </svg>
                <div className="flex items-center">
                  <div className="w-5 h-2.5 border border-white rounded-sm flex items-center p-0.5">
                    <div className="w-full h-full bg-white rounded-[1px]" />
                  </div>
                  <div className="w-0.5 h-1 bg-white rounded-r-sm ml-px" />
                </div>
              </div>
            </div>

            {/* Skeleton placeholder - renders instantly for fast LCP */}
            {(isLoading || !isNearViewport) && (
              <div className="absolute inset-0 z-[3] overflow-hidden bg-white flex flex-col items-center pt-12 px-6">
                {/* Blue header band */}
                <div className="w-full h-24 rounded-t-xl bg-gradient-to-r from-blue-500 to-blue-600 mb-8" />
                {/* Avatar circle */}
                <div className="w-20 h-20 rounded-full bg-muted/60 animate-pulse -mt-18 mb-4 border-4 border-white" />
                {/* Text lines */}
                <div className="w-24 h-3 bg-muted/50 rounded animate-pulse mb-2" />
                <div className="w-36 h-4 bg-muted/60 rounded animate-pulse mb-2" />
                <div className="w-44 h-3 bg-muted/40 rounded animate-pulse mb-6" />
                {/* QR code placeholder */}
                <div className="w-32 h-32 bg-muted/30 rounded-lg animate-pulse mb-6" />
                {/* Action buttons */}
                <div className="flex gap-6">
                  <div className="w-10 h-10 rounded-full bg-muted/40 animate-pulse" />
                  <div className="w-10 h-10 rounded-full bg-muted/40 animate-pulse" />
                  <div className="w-10 h-10 rounded-full bg-muted/40 animate-pulse" />
                </div>
              </div>
            )}

            {/* 
              PRELOADED IFRAMES - All profiles loaded but hidden
              This ensures instant switching between profiles
              
              KEY RENDERING OPTIMIZATIONS FOR RETINA DISPLAYS:
              - Scale transform is applied ONLY to the wrapper div, not the iframe
              - Iframe maintains native 375px width (matches iPhone viewport)
              - Content is rendered at native resolution then downsampled by CSS
              - Using contain: layout for stable dimensions
            */}

            {/* {isNearViewport &&
              PROFILE_URLS.map((profile, index) => {
                const profileContentHeight = profile.contentHeight ?? DEFAULT_PROFILE_CONTENT_HEIGHT;
                const isActive = index === selectedProfile;

                // PERFORMANCE: Only render the active profile iframe
                // Previously rendered default + Insurance + all viewed profiles simultaneously,
                // causing 2-4 heavy iframes (each ~4MB JS from qcardenterprise.com) to load at once.
                // This was the #1 cause of high TBT (1,000-3,000ms) on industry pages.

                
                // if (!isActive) return null;

                return (
                  <div
                    key={profile.id}
                    className="absolute top-0 left-0"
                    style={{
                      // Wrapper handles ALL transforms - never scale the iframe directly
                      width: `${375}px`,
                      height: `${profileContentHeight}px`,
                      transform: isActive
                        ? `scale(${iframeScale}) translateY(${-scrollOffset}px)`
                        : `scale(${iframeScale})`,
                      transformOrigin: "top left",
                      // GPU acceleration for smooth transforms
                      willChange: isActive ? "transform" : "auto",
                      // Crisp downsampling on retina displays
                      WebkitFontSmoothing: "antialiased",
                      // CLS FIX: strict containment prevents iframe content shifts
                      // from propagating to the parent page's CLS score
                      contain: "strict",
                      overflow: "hidden",
                      pointerEvents: "none",
                      // CLS FIX: Use display:none (not visibility:hidden) during settling.
                      // visibility:hidden still allows Lighthouse to measure layout shifts
                      // inside cross-origin iframes. display:none completely removes the
                      // element from the render tree, so no internal shifts are measured.
                      // The iframe still loads and fires onLoad while display:none.
                      // Since this wrapper is position:absolute with contain:strict,
                      // switching to display:block causes zero CLS.
                      display: isActive && !isLoading ? "block" : "none",
                    }}
                  > 
                    <iframe
                    src={profile.url}
                    title={`QCard Profile - ${profile.label}`}
                    onLoad={() => handleIframeLoad(index)}
                    // Active profile always gets high priority
                    {...{ fetchPriority: "high" } as any}
                    style={{
                      width: "375px",
                      height: `${profileContentHeight}px`,
                      maxWidth: "100%",
                      border: "none",
                      pointerEvents: "none",
                      display: "block",
                      imageRendering: "auto",
                      objectFit: "contain",
                      // Strict containment on iframe itself to isolate internal CLS
                      contain: "strict",
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    scrolling="no"
                    loading="eager"
                  /> 
                   </div>
                );
              })}  */}
            {isNearViewport &&
              PROFILE_IMAGES.map((profile, index) => {
                const isActive = index === selectedProfile;

                if (!isActive) return null;

                const profileContentHeight = profile.contentHeight ?? DEFAULT_PROFILE_CONTENT_HEIGHT;

                return (
                  <div
                    key={profile.id}
                    className="absolute top-0 left-0"
                    style={{
                      width: `${375}px`,
                      height: `${profileContentHeight}px`, // ✅ SAME AS IFRAME
                      transform: `scale(${iframeScale}) translateY(${-scrollOffset}px)`,
                      transformOrigin: "top left",
                      willChange: "transform",
                      contain: "strict",
                      overflow: "hidden",
                      pointerEvents: "none",
                      display: isActive ? "block" : "none",
                    }}
                  >
                    <img
                      src={profile.image}
                      alt={profile.label}
                      draggable={false}
                      onLoad={() => setIsLoading(false)}
                      style={{
                        width: "375px", // ✅ MATCH IFRAME WIDTH EXACTLY
                        height: `${profileContentHeight}px`, // ✅ CRITICAL
                        display: "block",
                        objectFit: "fill", // ✅ IMPORTANT (NOT contain/cover)
                        imageRendering: "auto",
                      }}
                    />
                  </div>
                );
              })}
            {/* Interactive hint indicator - fades out after first scroll */}
            <div
              className={`absolute top-12 left-1/2 -translate-x-1/2 z-20 pointer-events-none transition-opacity duration-500 ${
                hasScrolled || hasInteracted ? "opacity-0" : "opacity-100"
              }`}
            >
              <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm text-white text-xs font-medium px-3 py-2 rounded-full shadow-lg border border-white/20 animate-[pulse_2s_ease-in-out_infinite]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 1 1 3 0m-3 6a1.5 1.5 0 0 0-3 0v2a7.5 7.5 0 0 0 15 0v-5a1.5 1.5 0 0 0-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 0 1 3 0v1m0 0V11m0-5.5a1.5 1.5 0 0 1 3 0v3m0 0V11"
                  />
                </svg>
                <span>Drag to Scroll</span>
              </div>
            </div>

            {/* 
              INTERACTION OVERLAY
              - Captures all mouse/touch events for drag scrolling
              - When clickModeActive is true, pointer-events: none to allow clicks through to iframe
              - When dragging or normal state, captures events for scrolling
            */}
            <div
              className={`absolute inset-0 z-[5] rounded-[2.5rem] ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
              style={{
                pointerEvents: "auto",
              }}
              onMouseEnter={handleMouseEnter}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              // Touch events are handled via native listeners with passive: false
            />

            {/* Scroll indicator */}
            <div className="absolute right-1 top-10 bottom-10 w-1 z-20 pointer-events-none">
              <div
                className="w-full bg-white/30 rounded-full transition-opacity duration-300"
                style={{
                  height: `${thumbPercent}%`,
                  marginTop: `${thumbTopPercent}%`,
                  opacity: isDragging ? 0.8 : 0.3,
                }}
              />
            </div>

            {/* Home Indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full pointer-events-none z-10" />

            {/* Double-click Open Profile Dialog */}
            {showOpenDialog && (
              <div
                className="absolute inset-0 z-30 flex items-center justify-center rounded-[2.5rem]"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  backdropFilter: "blur(4px)",
                }}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl p-5 mx-4 max-w-[260px] w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-base font-semibold text-gray-900 text-center mb-2">Open Profile</h3>
                  <p className="text-sm text-gray-600 text-center mb-5">Open fully interactive profile in new tab</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowOpenDialog(false)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleOpenProfile}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-enterprise-purple hover:bg-enterprise-purple-dark rounded-lg transition-colors duration-200"
                    >
                      Open
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tap hint */}
      <p className="text-[11px] text-muted-foreground/60 text-center mt-2">Tap to open profile</p>

      {/* Profile Switching Buttons */}
      {showProfileSwitcher && (
        <div className="flex flex-col items-center gap-3 mt-4">
          <p className="text-xs font-medium text-muted-foreground">Select Sample Profile Industry</p>
          <div className="grid grid-cols-2 gap-2 w-full max-w-[280px]">
            {PROFILE_URLS.map((profile, index) => (
              <button
                key={profile.id}
                onClick={() => setSelectedProfile(index)}
                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  selectedProfile === index
                    ? "bg-enterprise-purple text-white border-enterprise-purple shadow-md"
                    : "bg-white/95 backdrop-blur-sm border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300"
                }`}
              >
                {profile.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground/70 text-center">
            *Profiles shown are sample profiles and
            <br />
            do not represent real individuals.
          </p>
        </div>
      )}
    </div>
  );
}

export default InteractivePhoneMockup;
